/**
 * Cruscotto della Pianificazione — «come sta il piano?».
 *
 * Fatto di **porte**, come quello di Commesse: nessuna tabella di lavorazioni,
 * solo i numeri d'insieme, e ognuno apre un'altra sezione già filtrata. È la
 * condizione che distingue un cruscotto da una vetrina, e di vetrine in questo
 * progetto ne abbiamo già chiuse due.
 *
 * La prima cosa che dice è **da quando è il piano**: era il difetto più grave
 * della pagina vecchia, che mostrava un piano di aprile chiamandolo «corrente».
 */
import { AlertTriangle, Boxes, RefreshCw, Sparkles, Upload } from "lucide-react";

import { RigaTesta } from "@/components/RigaTesta";
import { Porta } from "@/components/m2/Porta";
import { CaricoMacchine } from "@/components/m2/CaricoMacchine";
import { ClassificaStampi } from "@/components/m2/ClassificaStampi";
import { fmtMinuti, num } from "@/pages/M2/format";
import type { PianoResponse } from "@/types/m2";

/** Oltre questo, il piano non descrive più la settimana che si sta pianificando. */
const GIORNI_PIANO_VECCHIO = 30;

function giorniFa(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
}

function dataLunga(iso: string): string {
  return new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long" });
}

export function CruscottoM2({
  piano,
  onVai,
  onCarica,
}: {
  piano: PianoResponse;
  onVai: (sezione: "piano" | "simulazione" | "fabbisogno", filtro?: string) => void;
  onCarica: () => void;
}) {
  const k = piano.kpi;
  const hint = piano.ai_hint;
  // La finestra del piano dice di quando è il **lavoro**; `importato_il` dice
  // solo quando l'abbiamo riletto — e si sposta a ogni deploy, perché il
  // bootstrap rigira. Guardare la seconda avrebbe fatto sembrare fresco un piano
  // di aprile: è esattamente l'inganno che questa pagina deve togliere.
  const dataPiano = k.finestra_da;
  const eta = dataPiano ? giorniFa(dataPiano) : null;
  const vecchio = eta !== null && eta >= GIORNI_PIANO_VECCHIO;

  return (
    <div className="space-y-3">
      <RigaTesta
        contesto={`${num(k.lavorazioni_estrusione + k.lavorazioni_confezionamento)} lavorazioni su ${k.linee_totali} linee`}
        caricatoIl={dataPiano}
        etichettaData="piano del"
        soloGiorno
        titoloBadge="Il piano viene da un export del MES: più invecchia, meno le date e le durate descrivono la produzione di adesso"
      >
        <button
          type="button"
          onClick={onCarica}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-primary hover:opacity-80"
        >
          <Upload className="size-3.5" />
          Carica il piano MES
        </button>
      </RigaTesta>

      {vecchio && dataPiano && (
        <div className="flex flex-wrap items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3.5 dark:border-red-900 dark:bg-red-950/40">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-700 dark:text-red-400" />
          <div className="min-w-0 flex-1 text-[12.5px]">
            <div className="font-semibold text-red-800 dark:text-red-300">
              Il piano in uso è di {eta} giorni fa
            </div>
            <div className="mt-0.5 text-red-800/90 dark:text-red-300/90">
              Copre <b>{dataLunga(dataPiano)}</b>
              {k.finestra_a && <> → <b>{dataLunga(k.finestra_a)}</b></>}: date, durate e cambi
              stampo qui sotto descrivono quel periodo, non oggi. Carica l&apos;export del MES per
              lavorare sul piano vero.
            </div>
          </div>
          <button
            type="button"
            onClick={onCarica}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Upload className="size-3.5" />
            Carica il piano MES
          </button>
        </div>
      )}

      <CaricoMacchine linee={piano.linee} />

      <section className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Porta
          label="Cambi stampo nel piano"
          valore={k.cambi_stampo}
          sotto={`~${fmtMinuti(k.cambi_stampo_minuti)} di fermo macchina`}
          tono="attenzione"
          icona={RefreshCw}
          dove="apre il piano"
          titolo="Le lavorazioni di estrusione che montano una trafila diversa dalla precedente"
          onClick={() => onVai("piano", "cambi")}
        />
        <Porta
          label="Linee oltre capacità"
          valore={k.linee_oltre_capacita}
          sotto={`su ${k.linee_totali} con capacità nota`}
          tono={k.linee_oltre_capacita > 0 ? "allarme" : "ok"}
          icona={AlertTriangle}
          dove="apre il piano"
          titolo="Quantità del piano / (capacità giornaliera × giorni lavorati). Sopra il 100% il piano chiede più di quanto la macchina sa fare in quei giorni."
          onClick={() => onVai("piano")}
        />
        <Porta
          label="Recuperabile riordinando"
          valore={hint.cambi_stampo_eliminabili ?? 0}
          sotto={`cambi stampo · ${fmtMinuti(hint.minuti_recuperabili ?? 0)}`}
          tono={(hint.cambi_stampo_eliminabili ?? 0) > 0 ? "ok" : "neutro"}
          icona={Sparkles}
          dove="apre la simulazione"
          inerte={(hint.righe_riordinabili ?? 0) === 0}
          titolo="Stima dell'euristica a parità di quantità e vincoli: raggruppa per trafila e conta i cambi risparmiati"
          onClick={() => onVai("simulazione")}
        />
        <Porta
          label="Durate non credibili"
          valore={k.durate_sospette}
          sotto="da guardare riga per riga"
          tono={k.durate_sospette > 0 ? "allarme" : "ok"}
          icona={AlertTriangle}
          dove="apre il piano"
          inerte={k.durate_sospette === 0}
          titolo="Righe in cui la durata del MES non sta in piedi con la quantità e la velocità della famiglia"
          onClick={() => onVai("piano", "sospette")}
        />
      </section>

      <ClassificaStampi
        voci={piano.classifica_stampi}
        onApri={(stampo) => onVai("piano", `stampo:${stampo}`)}
      />

      {/*
        ⚠️ Questa frase **legge** lo stato, non lo afferma. Prima diceva «non è mai
        stato acceso»: il 17-08, verificando la produzione, si è scoperto che era
        acceso (1.361 righe, calcolate da un clic su «Reimporta») mentre la pagina
        raccontava il contrario. Uno stato scritto a memoria invecchia male.
      */}
      <div className="flex gap-2.5 rounded-lg border bg-muted/50 px-3.5 py-3 text-[12px] text-muted-foreground">
        <Boxes className="size-4 shrink-0" />
        {k.righe_con_data_disponibilita > 0 ? (
          <div>
            <b>Le date di consegna vengono da qui.</b> La Pianificazione calcola la data prevista di
            disponibilità di{" "}
            <b>{num(k.righe_con_data_disponibilita)} righe d&apos;ordine</b>, e Commesse e Spedizioni
            le leggono da questa. ⚠️ Sono ferme al piano con cui sono state calcolate: se il piano è
            vecchio, lo sono anche loro. Si ricalcolano dal Fabbisogno.
          </div>
        ) : (
          <div>
            <b>Cosa questo cruscotto non sa ancora dire:</b> se il piano arriva in tempo per le
            consegne. Il calcolo esiste — la Pianificazione è la fonte delle date di disponibilità —
            ma non è ancora acceso: oggi Commesse e Spedizioni usano il conto di M1.
            Si accende dal Fabbisogno, ed è un gesto che sposta numeri anche per la direzione.
          </div>
        )}
      </div>
    </div>
  );
}
